import classNames from "classnames";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import Button from "../../atomic/Button";
import { GroupVariant, NavbarRendererVariant, type GroupProps } from "./types";
import { useNavbarContext } from "./NavbarContext";
import { filterVisibleItems } from "./utils";
import { NavbarRenderer } from "./NavbarRenderer";

export function Group({
  item,
  variant = GroupVariant.Dropdown,
  onNavigate
}: GroupProps) {
  const { navigateFromSource } = useNavbarContext();
  const children = filterVisibleItems(item.children ?? []);
  const hasChildren = children.length > 0;
  const isClickable = Boolean(item.source);

  const handleHeaderClick = () => {
    if (item.source) {
      navigateFromSource(item.source);
      onNavigate?.();
    }
  };

  if (variant === GroupVariant.Mobile) {
    return (
      <div className="py-1">
        {item.label && (
          <div
            className={classNames(
              "px-4 py-2 text-xs font-semibold uppercase tracking-wide",
              isClickable && "cursor-pointer"
            )}
            style={{ color: "var(--navbar-text-hover)" }}
            onClick={isClickable ? handleHeaderClick : undefined}
            onKeyDown={
              isClickable
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") handleHeaderClick();
                  }
                : undefined
            }
            role={isClickable ? "button" : undefined}
            tabIndex={isClickable ? 0 : undefined}
          >
            {item.label}
          </div>
        )}
        <div className="pl-2">
          {children.map((child) => (
            <NavbarRenderer
              key={child.id}
              item={child}
              variant={NavbarRendererVariant.Mobile}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </div>
    );
  }

  if (hasChildren) {
    return (
      <div className="group relative w-full">
        <div
          className="flex w-full cursor-default items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          role="button"
          tabIndex={0}
        >
          <span>{item.label}</span>
          <ChevronRightIcon
            className="ml-2 h-4 w-4 shrink-0 text-gray-700"
            aria-hidden="true"
          />
        </div>
        <div className="invisible absolute left-full top-0 z-[60] min-w-[14rem] rounded-md bg-white py-1 text-gray-700 opacity-0 shadow-lg ring-1 ring-black ring-opacity-5 transition-all duration-100 group-hover:visible group-hover:opacity-100">
          {children.map((child) => (
            <NavbarRenderer
              key={child.id}
              item={child}
              variant={NavbarRendererVariant.Menu}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <Button
      onClick={isClickable ? handleHeaderClick : undefined}
      className={classNames(
        "block w-full rounded-none border-0 !bg-transparent px-4 py-2 text-left text-sm text-gray-700 shadow-none hover:!bg-gray-100",
        !isClickable && "cursor-default hover:!bg-transparent"
      )}
      pxFour={false}
    >
      {item.label}
    </Button>
  );
}
